import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const AnnaNotsuProfile = () => {
  const person = {
    id: 'anna-notsu',
    name: 'Anna Notsu',
    honorific: 'Ms',
    title: 'Highland Fellow',
    affiliation: 'PhD Scholar, Institute of Cultural Anthropology and Development Sociology (CADS), Leiden University',
    image: 'https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/g4ayizgt_Anna%20Notsu.jpg',
    email: 'a.notsu@fsw.leidenuniv.nl',
    category: 'Fellows & Visiting Scholars',
    testid: 'anna-notsu-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      {/* Intellectual Biography */}
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Anna Notsu is a PhD scholar at the Institute of Cultural Anthropology and Development Sociology (CADS) at Leiden University. She conducted research on climate change as a form of violence and how the idea of climate change embedded in people\'s everyday lives shapes the transformation of agriculture in Sicily.',
            'At present, she carries out research under the Futuring Heritage project, funded by the NWO Open-L grant. Her current work explores the intersection of environmental destruction and recuperation in the Indian state of Meghalaya, focusing on how nonhuman presences, including deities, influence the trajectory of future-making. She conducts fieldwork in the Jaintia Hills District among the Niamtre Pnar community.',
          ]}
        />
      </ProfileSection>

      {/* Research Foci & Areas of Work */}
      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileParagraphs
          items={[
            'Anna worked as a lecturer in Cultural Anthropology and Development Sociology at Leiden University before beginning her research career as a PhD scholar in the same department. Currently, her research interests are situated at the intersection of the anthropology of religion, environmental humanities and multimodal anthropology. Her work primarily engages with more-than-human relations, Indigenous belief systems, and environmentalism in the Indian state of Meghalaya.',
            'A key concern in her scholarship is the involvement of the dead, the dying, and death—including deities and ancestors—in shaping the ways in which the living engage with land and the Earth at large. She explores how such more-than-human relations are shaped by, and in turn shape, ecology, customary law, and political authority, challenging romanticised representations of environmentalism and cosmology among Indigenous communities.',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">Keywords</h3>
        <ProfileBullets
          items={[
            'More-than-human anthropology',
            'Environmental humanities',
            'Indigeneity',
            'Religion',
            'Multispecies ethnography',
            'Multimodality',
            'Matriliny',
            'Northeast India',
          ]}
        />
      </ProfileSection>

      {/* Highland Institute Projects */}
      <ProfileSection title="Highland Institute Projects">
        <ProfileParagraphs
          items={[
            'Anna brings her expertise in environmental anthropology and multimodal research methods to collaborative initiatives at The Highland Institute. Her fieldwork in the Jaintia Hills among the Niamtre Pnar community complements the Institute\'s focus on Indigenous knowledge systems, environmental change, and community-engaged scholarship in Northeast India.',
          ]}
        />
      </ProfileSection>

      {/* Selected Publications */}
      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <ProfilePublications
          items={[
            {
              year: '2025',
              citation: (
                <>
                  'Held in a story: Relatability across plates and places' in <em>Anthropology and Humanism</em>.
                </>
              ),
              url: 'https://anthrosource.onlinelibrary.wiley.com/doi/full/10.1111/anhu.70080',
            },
            {
              year: '2024',
              citation: (
                <>
                  'The Logic of Conservation: Rendering Community Participation in Jaintia Hills, Meghalaya' in <em>Community Involvement in Conservation and Livelihood Initiatives in the Eastern Himalayas</em>, Leiden University.
                </>
              ),
            },
            {
              year: '2024',
              citation: (
                <>
                  'To Be or Not to Be: Questioning Indigeneity' in <em>Raiot</em>.
                </>
              ),
              url: 'https://raiot.in/to-be-or-not-to-be-questioning-indigeneity/',
            },
            {
              year: '2024',
              citation: (
                <>
                  'When Stories Find Colour: The Art of Making Visual Stories' in <em>Lamjai</em>.
                </>
              ),
              url: 'https://lamjai.in/when-stories-find-colour-the-art-of-making-visual-stories/',
            },
            {
              year: '2023',
              citation: (
                <>
                  Book Review: 'Reworking Culture' in <em>Leiden University Research Output</em>.
                </>
              ),
            },
          ]}
        />
      </ProfileSection>

      {/* Public Engagement, Teaching & Community Work */}
      <ProfileSection title="Public Engagement, Teaching & Community Work">
        <ProfileParagraphs
          items={[
            'Alongside her academic career, Anna is also a published author of a children\'s book. The project began with a question: what happens when stories move across different forms such as drawing, translation and bookmaking to narrate who people are today through a story that does not begin with "Once upon a time"? She launched a co-publication project with the Biate community in eastern Meghalaya.',
            'The project emerged in response to the rapidly changing Biate environment, shaped by climate change and extensive coal mining and processing in the region. Yet, amid this ecological destruction, their long-valued practice of swidden farming has been blamed as the sole cause of deforestation. The Biate community found themselves confronting questions surrounding value, tradition, and future aspirations. In this context, heritage became an important yet challenging concept to explore for all involved.',
            'Now predominantly Christian, the community\'s negotiation between Christian faith and heritage has shaped their ways of engaging with the environment. These tensions and reflections prompted Anna\'s initiative to create a picture book that connects the past, present, and future.',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">In Media</h3>
        <ProfileBullets
          items={[
            '"Khâmnu Khah A Vâr Im?/Is She Wise?" - Children\'s book co-publication with the Biate community',
            'Meghalaya Monitor interview',
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-6 mb-4 not-prose">Related Publications</h3>
        <ProfileBullets
          items={[
            '"This is a Biate story"',
            'Binding Trust: Collaborative Storytelling through Image and Imagination',
            'Telling Together to Know Together: From Saipung to Shillong, and Beyond',
            'When Stories Find Colour: The Art of Making Visual Stories',
            'Returning to move forward: How the Biate revisit their past to shape their future',
          ]}
        />
      </ProfileSection>

      {/* Contact & Scholarly Infrastructure */}
      <ProfileContactSection
        email={person.email}
        role="PhD Scholar, Institute of Cultural Anthropology and Development Sociology, Leiden University; Highland Fellow"
        testidPrefix="anna"
        scholarlyProfiles={[
          { label: 'Leiden University Profile', url: 'https://www.universiteitleiden.nl/en/staffmembers/anna-notsu#tab-1', testid: 'anna-leiden-link' },
          { label: 'ORCID', url: 'https://orcid.org/0000-0003-1560-3114', testid: 'anna-orcid-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default AnnaNotsuProfile;
